import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './lib/firebase';
import { Login } from './pages/Login';
import { ActiveGame } from './pages/ActiveGame';
import { SessionPage } from './pages/SessionPage';
import { ListManagement } from './pages/ListManagement';
import { CommunityManagement } from './pages/CommunityManagement';
import { History } from './pages/History';
import { Dashboard } from './pages/Dashboard';
import { HostDashboard } from './pages/HostDashboard';
import { HospitalityManager } from './pages/HospitalityManager';
import { Registration } from './pages/Registration';
import { Confirmation } from './pages/Confirmation';
import { Demo } from './pages/Demo';
import { MasterLedger } from './pages/MasterLedger';
import { MasterLedgerPortal } from './pages/MasterLedgerPortal';
import { PlayerPage } from './pages/PlayerPage';
import { PublicRegistration } from './pages/PublicRegistration';
import { HospitalityDashboard } from './pages/HospitalityDashboard';
import { WhatsAppLink } from './pages/WhatsAppLink';
import { JoinGame } from './pages/JoinGame';
import { LanguageProvider } from './contexts/LanguageContext';
const ProtectedRoute = ({ user, children }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          {/* Login - allow staying on login if we are coming from demo */}
          <Route path="/login" element={<Login />} />

          {/* Host Dashboard (landing) */}
          <Route path="/dashboard" element={<ProtectedRoute user={user}><HostDashboard /></ProtectedRoute>} />

          {/* Session Manager */}
          <Route path="/sessions" element={<ProtectedRoute user={user}><Dashboard /></ProtectedRoute>} />

          {/* Session Details Page */}
          <Route path="/session/:id" element={<ProtectedRoute user={user}><SessionPage /></ProtectedRoute>} />
          <Route path="/session/:id/portal" element={<ProtectedRoute user={user}><MasterLedgerPortal /></ProtectedRoute>} />

          {/* Session Ledger (ActiveGame) */}
          <Route path="/session/:id/ledger/master" element={<ProtectedRoute user={user}><MasterLedger /></ProtectedRoute>} />
          <Route path="/session/:id/ledger/:tIndex" element={<ProtectedRoute user={user}><ActiveGame /></ProtectedRoute>} />
          <Route path="/session/:id/ledger" element={<ProtectedRoute user={user}><ActiveGame /></ProtectedRoute>} />
          <Route path="/dashboard/:sessionId" element={<ProtectedRoute user={user}><HospitalityDashboard /></ProtectedRoute>} />

          {/* List Management Page */}
          <Route path="/active-game" element={<ProtectedRoute user={user}><ListManagement /></ProtectedRoute>} />

          {/* Community */}
          <Route path="/community" element={<ProtectedRoute user={user}><CommunityManagement /></ProtectedRoute>} />
          <Route path="/player/:playerId" element={<ProtectedRoute user={user}><PlayerPage /></ProtectedRoute>} />

          {/* Session History */}
          <Route path="/history" element={<ProtectedRoute user={user}><History /></ProtectedRoute>} />

          {/* Hospitality Management */}
          <Route path="/hospitality" element={<ProtectedRoute user={user}><HospitalityManager /></ProtectedRoute>} />

          {/* WhatsApp agent linking (owner) */}
          <Route path="/whatsapp" element={<ProtectedRoute user={user}><WhatsAppLink /></ProtectedRoute>} />

          {/* Public join-by-code (player-facing) */}
          <Route path="/j/:code" element={<JoinGame />} />

          {/* Public Registration & Confirmation */}
          <Route path="/register/:hostId/:sessionId" element={<PublicRegistration />} />
          <Route path="/register/:sessionId" element={<Registration />} />
          <Route path="/join/:sessionId" element={<PublicRegistration />} />
          <Route path="/join" element={<PublicRegistration />} />
          <Route path="/confirm/:sessionId/:playerId" element={<Confirmation />} />

          {/* Legacy redirects */}
          <Route path="/game/:id" element={<Navigate to="/dashboard" replace />} />
          <Route path="/history/:id" element={<Navigate to="/dashboard" replace />} />

          {/* Marketing / Demo Route */}
          <Route path="/demo" element={<Demo />} />

          {/* Default */}
          <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
          <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
